import React from 'react';
import axios from 'axios';
import cloneDeep from 'lodash.clonedeep';
import { useParams } from 'react-router-dom';

import { API_V2_URL } from '../constants';
import { GameContext } from '../contexts/game';
import { getUrl } from '../services/http-client';
import { PlayerContext } from '../contexts/player';
import { addManualScoringAsync } from '../services/players';
import { getFromStore, putToStore } from '../utils/indexedDB';
import { UpdatePlayerStateAsync } from '../utils/game-engine/base';
import { GetTaskById as GetPlayerTaskById } from '../utils/game-engine/assets';

export type TUploadStatus = 'idle' | 'uploading' | 'failed' | 'complete';

export type TFileUploadItem = {
  taskId: string;
  formId: string;
  status: 'pending' | 'complete' | 'failed';
  isManualScoring?: boolean;
  base64Data?: string;
  fileName?: string;
  fileType?: string;
  lastModified?: number;
  lastUploadedChunk?: number;
  url?: string;
  lastError?: string;
  retryCount?: number;
  updatedAt?: number;
};

export type TFileUpload = {
  playerCode: string;
  files: TFileUploadItem[];
};

export type TUploadResponse = {
  status: 'in-progress' | 'complete';
  response: {
    name: string;
    extension: string;
    contextSize: number;
    mimeType: string;
    url: string;
  };
};

// slice per 3mb
const CHUNK_SIZE = 1024 * 1024 * 3;
const CHUNK_TIMEOUT_MS = 60000;
const MAX_CHUNK_RETRIES = 3;
const RETRY_BACKOFF_BASE_MS = 1000;

interface ActiveUploadControl {
  abortController: AbortController;
  startedAt: number;
}

// Global in-memory lock map to prevent duplicate / concurrent uploads of the same task form
export const activeUploads = new Map<string, ActiveUploadControl>();

export const getUploadKey = (
  playerCode: string,
  taskId: string,
  formId: string
) => `${playerCode}:${taskId}:${formId}`;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new Error('Upload aborted'));
    const timer = setTimeout(() => resolve(), ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new Error('Upload aborted'));
      },
      { once: true }
    );
  });

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const base64ToBlob = (base64: string): Blob => {
  const [header, data] = base64.split(',');
  const mimeMatch = header.match(/data:(.*);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
};

export const sliceFile = (file: File): Blob[] => {
  const chunks: Blob[] = [];
  let offset = 0;
  while (offset < file.size) {
    const end = Math.min(offset + CHUNK_SIZE, file.size);
    chunks.push(file.slice(offset, end));
    offset = end;
  }
  return chunks;
};

export const uploadChunk = async (
  gameCode: string,
  chunk: Blob,
  index: number,
  totalChunks: number,
  fileId: string,
  filename: string,
  fileType: string,
  signal?: AbortSignal
) => {
  const formData = new FormData();
  formData.append('chunk', chunk);
  formData.append('fileId', fileId);
  formData.append('chunkNumber', index.toString());
  formData.append('totalChunks', totalChunks.toString());
  formData.append('fileName', filename);
  formData.append('fileType', fileType);

  const url = getUrl(`${API_V2_URL.games}${gameCode}/players/upload-chunk`);

  return axios.post<TUploadResponse>(url, formData, {
    timeout: CHUNK_TIMEOUT_MS,
    signal
  });
};

export const uploadChunkWithRetry = async (
  gameCode: string,
  chunk: Blob,
  index: number,
  totalChunks: number,
  fileId: string,
  filename: string,
  fileType: string,
  signal?: AbortSignal,
  maxRetries = MAX_CHUNK_RETRIES
) => {
  let attempt = 0;
  while (true) {
    try {
      if (signal?.aborted) {
        throw new Error('Upload aborted');
      }
      return await uploadChunk(
        gameCode,
        chunk,
        index,
        totalChunks,
        fileId,
        filename,
        fileType,
        signal
      );
    } catch (err: any) {
      attempt++;
      if (axios.isCancel(err) || signal?.aborted) {
        throw err;
      }
      if (!navigator.onLine) {
        throw err;
      }
      if (
        err.response?.status &&
        err.response.status >= 400 &&
        err.response.status < 500
      ) {
        throw err;
      }
      if (attempt > maxRetries) {
        throw err;
      }
      const backoff = RETRY_BACKOFF_BASE_MS * Math.pow(2, attempt - 1);
      console.warn(
        `[useUploadChunk] Chunk ${index}/${totalChunks} failed (attempt ${attempt}/${maxRetries}). Retrying in ${backoff}ms...`,
        err?.message || err
      );
      await sleep(backoff, signal);
    }
  }
};

export const useUploadChunk = ({
  skipExecuteInTask,
  onSuccessCallback
}: {
  skipExecuteInTask?: boolean;
  onSuccessCallback?: (res: TUploadResponse['response']) => void;
}) => {
  const { gameCode } = useParams();
  const [, , , isOnline] = React.useContext(GameContext);
  const [player, setPlayer] = React.useContext(PlayerContext);
  const [isUploading, setIsUploading] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState<
    Record<string, TUploadStatus>
  >({});
  const [uploadErrors, setUploadErrors] = React.useState<
    Record<string, string | null>
  >({});

  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const playerCode = player.playerState?.code || '';
  const openedTaskId = (player.playerState?.openedTask || []).at(0);
  const openedOnDemandTaskId = player.playerState?.openedOnDemandTask;

  const updateAnswer = async (
    url: string,
    taskId: string,
    formId: string,
    isManualScoring: boolean
  ) => {
    const selectedTask = cloneDeep(GetPlayerTaskById(player, taskId));
    if (selectedTask && selectedTask.taskContentFormAnswers) {
      const answerFormId = selectedTask.taskContentFormAnswers.findIndex(
        (a) => a.formId === formId
      );
      if (answerFormId !== -1) {
        const answerForm = selectedTask.taskContentFormAnswers[answerFormId];
        answerForm.answers = [{ answerResId: '', value: url }];
        setPlayer((prev) => {
          if (!prev.playerState) return prev;
          if (prev.playerState?.tasks) {
            const taskIndex = prev.playerState.tasks.findIndex(
              (t) => t.id === taskId
            );
            if (taskIndex !== -1)
              prev.playerState.tasks[taskIndex] = selectedTask;
          }

          return UpdatePlayerStateAsync(prev, prev.playerState);
        });

        // ONLY call addManualScoringAsync if the task form explicitly uses manual scoring!
        if (!isManualScoring) return;
        try {
          await addManualScoringAsync(
            gameCode,
            playerCode,
            taskId,
            formId,
            JSON.stringify(answerForm)
          );
        } catch (scoringError) {
          console.error(
            '[useUploadChunk] Failed to submit manual scoring after upload:',
            scoringError
          );
        }
      }
    }
  };

  const startUpload = async (
    file: File,
    taskId: string,
    formId: string,
    isManualScoring = false,
    resumeFromChunk = 0,
    isUserInitiated = false
  ): Promise<TUploadResponse['response'] | undefined> => {
    const uploadKey = getUploadKey(playerCode, taskId, formId);
    const formKey = `${taskId}_${formId}`;

    // Concurrency lock check
    const existing = activeUploads.get(uploadKey);
    if (existing) {
      if (isUserInitiated) {
        console.info(
          `[useUploadChunk] Aborting prior upload for ${uploadKey} due to new user file selection`
        );
        existing.abortController.abort();
        activeUploads.delete(uploadKey);
      } else {
        console.info(
          `[useUploadChunk] Upload already in progress for ${uploadKey}. Skipping duplicate start.`
        );
        return;
      }
    }

    const abortController = new AbortController();
    activeUploads.set(uploadKey, {
      abortController,
      startedAt: Date.now()
    });

    if (isMountedRef.current) {
      setIsUploading(true);
      setUploadStatus((prev) => ({ ...prev, [formKey]: 'uploading' }));
      setUploadErrors((prev) => ({ ...prev, [formKey]: null }));
    }

    const chunks = sliceFile(file);

    // Prevent poison resume state where chunk index >= total chunks
    if (resumeFromChunk < 0 || resumeFromChunk >= chunks.length) {
      if (resumeFromChunk >= chunks.length && chunks.length > 0) {
        console.warn(
          `[useUploadChunk] resumeFromChunk (${resumeFromChunk}) >= chunks.length (${chunks.length}). Resetting to 0 to prevent hang.`
        );
      }
      resumeFromChunk = 0;
    }

    try {
      let finalResponse: TUploadResponse['response'] | undefined;

      for (let i = resumeFromChunk; i < chunks.length; i++) {
        if (abortController.signal.aborted) {
          throw new Error('Upload aborted');
        }

        const fileName = file.name;
        const res = await uploadChunkWithRetry(
          gameCode ?? '',
          chunks[i],
          i + 1,
          chunks.length,
          `${playerCode}-${taskId}-${formId}`,
          fileName,
          file.type,
          abortController.signal
        );

        // Update progress in IndexedDB
        const uploads = await getFromStore<TFileUpload>(
          'upload-queue',
          playerCode
        );
        if (uploads) {
          const selectedFileIndex = uploads.files.findIndex(
            (f) => f.taskId === taskId && f.formId === formId
          );

          if (selectedFileIndex !== -1) {
            const selectedFile = cloneDeep(uploads.files[selectedFileIndex]);
            selectedFile.lastUploadedChunk = i + 1;
            selectedFile.updatedAt = Date.now();

            if (res.data.status === 'complete') {
              selectedFile.status = 'complete';
              selectedFile.url = res.data.response.url;
              uploads.files[selectedFileIndex] = selectedFile;
              await putToStore('upload-queue', uploads);

              await updateAnswer(
                res.data.response.url,
                taskId,
                formId,
                isManualScoring
              );

              finalResponse = res.data.response;
              onSuccessCallback?.(res.data.response);
              break;
            } else {
              uploads.files[selectedFileIndex] = selectedFile;
              await putToStore('upload-queue', uploads);
            }
          }
        }
      }

      // If loop completed all chunks but didn't receive 'complete' status
      if (!finalResponse) {
        throw new Error(
          'Server did not return complete status after sending all chunks.'
        );
      }

      if (isMountedRef.current) {
        setUploadStatus((prev) => ({ ...prev, [formKey]: 'complete' }));
      }
      return finalResponse;
    } catch (err: any) {
      if (axios.isCancel(err) || abortController.signal.aborted) {
        console.info('[useUploadChunk] Upload cancelled.');
        if (isMountedRef.current) {
          setUploadStatus((prev) => ({ ...prev, [formKey]: 'idle' }));
        }
        return;
      }

      const isNetworkOrOffline =
        !navigator.onLine ||
        !isOnline ||
        err?.message === 'Network Error' ||
        err?.code === 'ERR_NETWORK' ||
        err?.code === 'ECONNABORTED';

      const errorMessage = isNetworkOrOffline
        ? 'Network connection lost. Upload paused and will resume automatically when back online.'
        : err?.response?.data?.message ||
          err?.message ||
          'Upload failed. Please check your network and retry.';

      console.warn('[useUploadChunk] Upload interrupted:', {
        isNetworkOrOffline,
        err
      });

      // Crucial for offline-first: If interrupted by offline/network drop,
      // DO NOT mark as 'failed' in IndexedDB! Keep status as 'pending'
      // so background and task auto-resume pick it up automatically when back online.
      try {
        const uploads = await getFromStore<TFileUpload>(
          'upload-queue',
          playerCode
        );
        if (uploads) {
          const selectedFileIndex = uploads.files.findIndex(
            (f) => f.taskId === taskId && f.formId === formId
          );
          if (selectedFileIndex !== -1) {
            uploads.files[selectedFileIndex].status = isNetworkOrOffline
              ? 'pending'
              : 'failed';
            uploads.files[selectedFileIndex].lastError = errorMessage;
            uploads.files[selectedFileIndex].updatedAt = Date.now();
            await putToStore('upload-queue', uploads);
          }
        }
      } catch (idbErr) {
        console.error('[useUploadChunk] Failed to persist upload status:', idbErr);
      }

      if (isMountedRef.current) {
        setUploadStatus((prev) => ({ ...prev, [formKey]: 'failed' }));
        setUploadErrors((prev) => ({ ...prev, [formKey]: errorMessage }));
      }
      return;
    } finally {
      activeUploads.delete(uploadKey);
      if (isMountedRef.current) {
        setIsUploading(false);
      }
    }
  };

  const handleFileChange = async (
    file: File,
    taskId: string,
    formId: string,
    isManualScoring: boolean
  ) => {
    if (!playerCode) return;
    if (!file) return;

    const fileName = file.name;
    let base64 = '';
    try {
      base64 = await fileToBase64(file);
    } catch (err) {
      console.warn('[useUploadChunk] fileToBase64 error, continuing:', err);
    }

    try {
      let uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads) {
        uploads = { playerCode, files: [] };
      }

      const selectedFileIndex = uploads.files.findIndex(
        (f) => f.formId === formId && f.taskId === taskId
      );

      const storedFile: TFileUploadItem = {
        taskId,
        formId,
        status: 'pending',
        isManualScoring,
        fileName,
        base64Data: base64,
        fileType: file.type,
        lastModified: file.lastModified,
        lastUploadedChunk: 0,
        retryCount: 0,
        updatedAt: Date.now()
      };

      if (selectedFileIndex === -1) {
        uploads.files.push(storedFile);
      } else {
        uploads.files[selectedFileIndex] = storedFile;
      }

      await putToStore('upload-queue', uploads);
    } catch (idbErr) {
      console.warn(
        '[useUploadChunk] Could not write to upload-queue (IDB storage quota?), proceeding to upload directly:',
        idbErr
      );
    }

    return await startUpload(
      file,
      taskId,
      formId,
      isManualScoring,
      0,
      true
    );
  };

  const retryUpload = async (taskId: string, formId: string) => {
    if (!playerCode) return;
    try {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads) return;
      const fileItem = uploads.files.find(
        (f) => f.taskId === taskId && f.formId === formId
      );
      if (!fileItem || !fileItem.base64Data) {
        console.warn('[retryUpload] No file data found in queue to retry');
        return;
      }

      const blob = base64ToBlob(fileItem.base64Data);
      const fileObj = new File([blob], fileItem.fileName ?? 'upload.jpg', {
        type: fileItem.fileType || 'image/jpeg',
        lastModified: fileItem.lastModified
      });

      // Always restart from chunk 0 on explicit retry to avoid corrupt server states
      fileItem.status = 'pending';
      fileItem.lastUploadedChunk = 0;
      fileItem.lastError = undefined;
      fileItem.updatedAt = Date.now();
      await putToStore('upload-queue', uploads);

      return await startUpload(
        fileObj,
        taskId,
        formId,
        fileItem.isManualScoring ?? false,
        0,
        true
      );
    } catch (err) {
      console.error('[retryUpload] Failed to retry upload:', err);
    }
  };

  const cancelUpload = async (taskId: string, formId: string) => {
    const uploadKey = getUploadKey(playerCode, taskId, formId);
    const existing = activeUploads.get(uploadKey);
    if (existing) {
      existing.abortController.abort();
      activeUploads.delete(uploadKey);
    }
    const formKey = `${taskId}_${formId}`;
    if (isMountedRef.current) {
      setIsUploading(false);
      setUploadStatus((prev) => ({ ...prev, [formKey]: 'idle' }));
    }
  };

  // Auto-resume pending files for current opened task
  React.useEffect(() => {
    if (skipExecuteInTask) return;
    if (!isOnline) return;
    if (!playerCode) return;
    if (!openedTaskId && !openedOnDemandTaskId) return;

    (async () => {
      const uploads = await getFromStore<TFileUpload>(
        'upload-queue',
        playerCode
      );
      if (!uploads || uploads.files.length === 0) return;

      const targetTaskId = openedTaskId || openedOnDemandTaskId;
      const pendingFiles = uploads.files.filter(
        (f) => f.status === 'pending' && f.base64Data && f.taskId === targetTaskId
      );

      for (const file of pendingFiles) {
        const key = getUploadKey(playerCode, file.taskId, file.formId);
        if (activeUploads.has(key)) continue;

        const blob = base64ToBlob(file.base64Data!);
        const fileObj = new File([blob], file.fileName ?? '', {
          type: file.fileType,
          lastModified: file.lastModified
        });

        await startUpload(
          fileObj,
          file.taskId,
          file.formId,
          file.isManualScoring,
          file.lastUploadedChunk || 0
        );
      }
    })();
  }, [
    skipExecuteInTask,
    isOnline,
    playerCode,
    openedTaskId,
    openedOnDemandTaskId
  ]);

  return {
    isUploading,
    uploadStatus,
    uploadErrors,
    startUpload,
    handleFileChange,
    retryUpload,
    cancelUpload
  };
};
